import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import CloseIcon from '@/public/icons/close_black.svg';
import WorkHoursIcon from '@/public/icons/mypage/WorkHours.svg';
import TimeIcon from '@/public/icons/mypage/OvertimeTime.svg';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { fetchApi } from '@/utils/api/customAxios';
import { useSnackbar } from '@/hooks/global-tools';
import { AxiosError } from 'axios';
import { useGetTimeAdjustmentSummary } from '@/hooks/apiHooks';
import Label from '@/components/@IntranetLibrary/Label';
import BaseSelect from '@/components/@IntranetLibrary/Select';
import LeaveDatePicker from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/WorkHours/LeaveDatePicker';
import usePlatform from '@/components/Record/hooks/usePlatform';
import { useTranslation } from 'react-i18next';
import {
  approvalDialogActionsSx,
  approvalDialogPaperSx,
  getApprovalDialogActionButtonSx,
} from '@/components/MenuDrawer/MyMenuDrawer/SubMenu/approvalDialogStyles';

const formatDate = (d: Date) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;

const getToday = () => formatDate(new Date());

const timeToMinutes = (time: string): number => {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const minutesToTime = (minutes: number): string => {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const generateTimeOptions = (
  fromMinutes: number,
  toMinutes: number,
  excludeRanges?: [number, number][],
): string[] => {
  const options: string[] = [];
  for (let t = fromMinutes; t <= toMinutes; t += 30) {
    if (excludeRanges?.some(([lo, hi]) => t >= lo && t < hi)) continue;
    options.push(minutesToTime(t));
  }
  return options;
};

const parseWorkStartTime = (workingHours: string): string => {
  const match = workingHours.match(/^(\d{2}:\d{2})/);
  return match ? match[1] : '09:00';
};

const parseWorkEndTime = (workingHours: string): string => {
  const match = workingHours.match(/~(\d{2}:\d{2})/);
  return match ? match[1] : '18:00';
};

const toOptions = (times: string[]) =>
  times.map((t) => ({ label: t, value: t }));

type Props = {
  open: boolean;
  onClose: () => void;
  workingHours?: string;
  onSuccess?: () => void;
};

const OvertimeAdjustModal = ({
  open,
  onClose,
  workingHours = '09:00~18:00',
  onSuccess,
}: Props) => {
  const { t } = useTranslation();
  const { isMobilePath } = usePlatform();
  const actionButtonSx = getApprovalDialogActionButtonSx(isMobilePath);
  const workStartTime = parseWorkStartTime(workingHours);
  const workEndTime = parseWorkEndTime(workingHours);
  const workStartMinutes = timeToMinutes(workStartTime);
  const workEndMinutes = timeToMinutes(workEndTime);

  const [isExpiryOpen, setIsExpiryOpen] = useState(false);
  const [date, setDate] = useState(getToday);
  const [startTime, setStartTime] = useState(workStartTime);
  const [endTime, setEndTime] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { showSuccess, showError } = useSnackbar();

  const { data, mutate } = useGetTimeAdjustmentSummary();

  useEffect(() => {
    if (!open) return;
    setDate(getToday());
    setStartTime(workStartTime);
    setEndTime('');
  }, [open, workStartTime, workEndTime]);

  const totalRemainingHours =
    data?.expiringOvertimes?.reduce((sum, o) => sum + o.remainingHours, 0) ?? 0;

  const startTimeOptions = generateTimeOptions(
    workStartMinutes,
    workEndMinutes - 30,
    [[720, 780]],
  );

  const endTimeOptions = (() => {
    if (!startTime) return [];
    const st = timeToMinutes(startTime);
    const simpleEnd = st + 120;
    const lunchTouched = Math.max(
      0,
      Math.min(simpleEnd, 780) - Math.max(st, 720),
    );
    const fullLunchOverlap = lunchTouched > 0 ? 780 - Math.max(st, 720) : 0;
    const maxEnd = Math.min(st + 120 + fullLunchOverlap, workEndMinutes);
    return generateTimeOptions(st + 30, maxEnd, [[750, 780]]);
  })();

  const usedMinutes = (() => {
    if (!startTime || !endTime) return null;
    const startMin = timeToMinutes(startTime);
    const endMin = timeToMinutes(endTime);
    const clockDiff = endMin - startMin;
    if (clockDiff <= 0) return null;
    const lunchOverlap = Math.max(
      0,
      Math.min(endMin, 780) - Math.max(startMin, 720),
    );
    return clockDiff - lunchOverlap;
  })();

  const usedTimeText = (() => {
    if (usedMinutes === null) return '';
    const h = Math.floor(usedMinutes / 60);
    const m = usedMinutes % 60;
    if (h === 0) return t('MYPAGE.{{minutes}}분', { minutes: m });
    if (m === 0) return t('MYPAGE.{{hours}}시간', { hours: h });
    return t('MYPAGE.{{hours}}시간 {{minutes}}분', { hours: h, minutes: m });
  })();

  const handleStartTimeChange = (newTime: string) => {
    setStartTime(newTime);
    setEndTime('');
  };

  const isSubmitDisabled = !startTime || !endTime || isLoading;

  const handleSubmit = async () => {
    if (!startTime || !endTime) return;
    try {
      setIsLoading(true);
      await fetchApi({
        method: 'POST',
        url: '/api/v1/passport/me/toil-time-adjustment',
        data: {
          startedAt: `${date} ${startTime}:00`,
          endedAt: `${date} ${endTime}:00`,
        },
      });
      showSuccess({ message: t('MYPAGE.신청이 완료되었습니다.') });
      onSuccess?.();
      onClose();
    } catch (e) {
      const message =
        e instanceof AxiosError
          ? e.response?.data?.message ?? t('MYPAGE.신청에 실패했습니다.')
          : t('MYPAGE.신청에 실패했습니다.');
      showError({ message });
    } finally {
      setIsLoading(false);
      mutate();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullScreen={isMobilePath}
      PaperProps={!isMobilePath ? { sx: approvalDialogPaperSx } : undefined}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          position: 'relative',
          padding: '12px 20px',
          fontSize: '16px',
          fontWeight: 500,
          backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
          borderBottom: '1px solid var(--dk-line, #E7E9EC)',
          lineHeight: '24px',
        }}
      >
        {t('MYPAGE.연장근무 적립 사용')}
        <Button
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: '12px',
            top: '8px',
            padding: '4px',
            minWidth: 'auto',
          }}
        >
          <CloseIcon width="24px" height="24px" />
        </Button>
      </DialogTitle>

      <DialogContent sx={{ padding: '24px 20px 40px', overflowY: 'auto' }}>
        {/* 안내 문구 */}
        <Box sx={{ mt: '20px', mb: '20px' }}>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: 'var(--dk-text-secondary, #888D96)',
              fontWeight: 400,
            }}
          >
            {t(
              'MYPAGE.근태 품의는 별도의 결재 없이 즉시 승인되며, 팀장을 포함한 소속 팀원에게 자동 공유됩니다.',
            )}
            <br />
            {t(
              'MYPAGE.원활한 업무 진행을 위해 팀장 및 동료에게 사전 공유를 권장 드립니다.',
            )}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: 'var(--dk-accent, #0066CC)',
              marginTop: '24px',
              fontWeight: 400,
            }}
          >
            {t('MYPAGE.적립시간은 발생일 기준 3개월 후 자동 소멸됩니다.')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: 'var(--dk-text-secondary, #888D96)',
              fontWeight: 400,
            }}
          >
            {t(
              'MYPAGE.사전 또는 당일 신청을 원칙으로 합니다. 사후 신청은 불가합니다.',
            )}
            <br />
            {t('MYPAGE.외출 및 복귀 시 1층 스피드게이트 이용을 권장합니다.')}
            <br />
            {t(
              'MYPAGE.비상계단·지하주차장·비상엘리베이터 등 이용 시에도 반드시 인근 출입 리더기에 태깅해 주시기 바랍니다.',
            )}
            <br />
            {t('MYPAGE.사용 시 먼저 적립된 시간부터 자동 차감됩니다.')}
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              lineHeight: '20px',
              color: 'var(--dk-danger, #F55060)',
              fontWeight: 500,
              marginTop: '24px',
            }}
          >
            {t(
              'MYPAGE.사실과 다르게 신청할 경우, 인사 규정에 따라 불이익이 발생할 수 있습니다.',
            )}
          </Typography>
        </Box>
        {/* 연장 근로 시간 현황 */}
        <Box sx={{ mb: '24px' }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              color: 'var(--dk-text, #000)',
              mb: '8px',
            }}
          >
            {t('MYPAGE.연장근무 사용 현황')}
          </Typography>
          <Box
            sx={{
              border: '1px solid var(--dk-line, #E7E9EC)',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            {[
              {
                label: t('MYPAGE.이번주 신청 횟수'),
                value: t('MYPAGE.{{n}} / 4회', {
                  n: data?.weeklyRequestCount ?? '-',
                }),
                valueColor: 'var(--dk-text, #000)',
                emphasized: false,
              },
              {
                label: t('MYPAGE.사용 가능 시간'),
                value: t('MYPAGE.{{hours}}시간', {
                  hours: data?.remainingHours ?? '-',
                }),
                valueColor: 'var(--dk-accent, #0066CC)',
                emphasized: true,
              },
            ].map((row, i, arr) => (
              <Box
                key={row.label}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  borderBottom:
                    i < arr.length - 1
                      ? '1px solid var(--dk-line, #E7E9EC)'
                      : 'none',
                }}
              >
                <Box
                  sx={{
                    width: '20%',
                    minWidth: 160,
                    padding: '8px',
                    backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                    borderRight: '1px solid var(--dk-line, #E7E9EC)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: '500',
                      lineHeight: '20px',
                    }}
                  >
                    {row.label}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    padding: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '14px',
                      color: row.valueColor,
                      fontWeight: row.emphasized ? 700 : 400,
                    }}
                  >
                    {row.value}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
        {/* 2주 이내 소멸 예정 */}
        {totalRemainingHours > 0 && (
          <Box sx={{ mb: '24px' }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                cursor: 'pointer',
                mb: '4px',
              }}
              onClick={() => setIsExpiryOpen((p) => !p)}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: 'var(--dk-text, #000)',
                  lineHeight: '20px',
                }}
              >
                {t('MYPAGE.2주 이내 소멸 예정')}{' '}
                <Box
                  component="span"
                  sx={{ color: 'var(--dk-danger, #F55060)', fontWeight: '700' }}
                >
                  {t('MYPAGE.{{hours}}시간', { hours: totalRemainingHours })}
                </Box>
              </Typography>
              {isExpiryOpen ? (
                <KeyboardArrowUpIcon
                  sx={{
                    color: 'var(--dk-text-secondary, #888D96)',
                    fontSize: '20px',
                  }}
                />
              ) : (
                <KeyboardArrowDownIcon
                  sx={{
                    color: 'var(--dk-text-secondary, #888D96)',
                    fontSize: '20px',
                  }}
                />
              )}
            </Box>
            {isExpiryOpen && (
              <>
                <Typography
                  sx={{
                    fontSize: '14px',
                    color: 'var(--dk-text-secondary, #888D96)',
                    mb: '8px',
                    lineHeight: '28px',
                  }}
                >
                  {t(
                    'MYPAGE.만료일은 발생일 기준 3개월 후 동일 일자로, 해당 일자까지 사용 가능합니다.',
                  )}
                </Typography>

                <Box
                  component="table"
                  sx={{
                    width: '100%',
                    borderCollapse: 'separate',
                    borderSpacing: 0,
                    fontSize: '14px',
                    '& th, & td': {
                      borderRight: '1px solid var(--dk-line, #E7E9EC)',
                      borderBottom: '1px solid var(--dk-line, #E7E9EC)',
                    },
                    '& th:first-of-type, & td:first-of-type': {
                      borderLeft: '1px solid var(--dk-line, #E7E9EC)',
                    },
                    '& thead tr th': {
                      borderTop: '1px solid var(--dk-line, #E7E9EC)',
                    },
                    '& thead tr th:first-of-type': {
                      borderTopLeftRadius: '8px',
                    },
                    '& thead tr th:last-of-type': {
                      borderTopRightRadius: '8px',
                    },
                    '& tbody tr:last-of-type td:first-of-type': {
                      borderBottomLeftRadius: '8px',
                    },
                    '& tbody tr:last-of-type td:last-of-type': {
                      borderBottomRightRadius: '8px',
                    },
                  }}
                >
                  <Box
                    component="thead"
                    sx={{
                      backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
                      color: 'var(--dk-text-secondary, #888D96)',
                    }}
                  >
                    <tr>
                      {[
                        t('MYPAGE.적립일'),
                        t('MYPAGE.만료일'),
                        t('MYPAGE.적립'),
                        t('MYPAGE.잔여'),
                      ].map((h) => (
                        <Box
                          component="th"
                          key={h}
                          sx={{
                            fontSize: '14px',
                            lineHeight: '20px',
                            padding: '8px',
                            textAlign: 'center',
                            fontWeight: 500,
                            color: 'var(--dk-text, black)',
                          }}
                        >
                          {h}
                        </Box>
                      ))}
                    </tr>
                  </Box>
                  <tbody>
                    {(data?.expiringOvertimes ?? []).map((row, i) => (
                      <tr key={i}>
                        {[
                          row.startedAt.slice(0, 10),
                          row.expiringAt.slice(0, 10),
                          t('MYPAGE.{{hours}}시간', { hours: row.totalHours }),
                          t('MYPAGE.{{hours}}시간', {
                            hours: row.remainingHours,
                          }),
                        ].map((val, j) => (
                          <Box
                            component="td"
                            key={j}
                            sx={{
                              padding: '10px 8px',
                              textAlign: 'center',
                            }}
                          >
                            {val}
                          </Box>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Box>
              </>
            )}
          </Box>
        )}
        {/* 섹션 구분 Divider — DialogContent 좌우 20px 패딩을 상쇄해 모달 끝까지 full-bleed */}
        <Box
          sx={{
            mx: '-20px',
            height: '8px',
            backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
            mb: '24px',
          }}
        />
        {/* 신청 시간 */}
        <Box sx={{ mb: '20px' }}>
          <Typography
            sx={{
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '24px',
              color: 'var(--dk-text, #000)',
              mb: '12px',
            }}
          >
            {t('MYPAGE.신청 시간')}
          </Typography>

          {/* 신청일시 */}
          <Box
            sx={{
              mb: '12px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <Label isRequired={true} title={t('MYPAGE.신청일시')} size={14} />
            {/* 날짜: 풀폭 (위) — 연차 신청과 동일한 LeaveCalendar 팝업(단일 날짜) */}
            <LeaveDatePicker
              value={date}
              onChange={setDate}
              minDate={getToday()}
            />

            {/* 시간 선택: 시작 ~ 종료 (날짜 아래, 1:1) */}
            <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {/* 시작 시간 */}
              <BaseSelect
                value={startTime}
                options={toOptions(startTimeOptions)}
                onChange={(e) =>
                  handleStartTimeChange(e.target.value as string)
                }
                placeholder="HH:MM"
                IconComponent={WorkHoursIcon}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '&&': { height: '44px' },
                  '& .MuiInputBase-input': {
                    minHeight: 0,
                    paddingRight: '40px !important',
                  },
                  '& .MuiSelect-icon': {
                    transform: 'none',
                    top: '50%',
                    marginTop: '-12px',
                  },
                }}
              />

              <Typography
                sx={{
                  color: 'var(--dk-text-secondary, #888D96)',
                  flexShrink: 0,
                }}
              >
                ~
              </Typography>

              {/* 종료 시간 */}
              <BaseSelect
                value={endTime}
                options={toOptions(endTimeOptions)}
                onChange={(e) => setEndTime(e.target.value as string)}
                placeholder="HH:MM"
                disabled={!startTime}
                IconComponent={WorkHoursIcon}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  '&&': { height: '44px' },
                  '& .MuiInputBase-input': {
                    minHeight: 0,
                    paddingRight: '40px !important',
                  },
                  '& .MuiSelect-icon': {
                    transform: 'none',
                    top: '50%',
                    marginTop: '-12px',
                  },
                }}
              />
            </Box>
          </Box>

          <Box
            sx={{
              backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
              borderRadius: '8px',
              padding: '12px',
              mb: '8px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                flexWrap: 'wrap',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  color: 'var(--dk-text-strong, #555)',
                  lineHeight: '20px',
                }}
              >
                {t('MYPAGE.사용 시간')}
              </Typography>
              {usedTimeText && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <TimeIcon width={24} height={24} />
                  <Box
                    component="span"
                    sx={{
                      fontSize: '16px',
                      fontWeight: 500,
                      color: 'var(--dk-danger, #F55060)',
                    }}
                  >
                    {usedTimeText}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>

          <Typography
            sx={{
              fontSize: '14px',
              color: 'var(--dk-text-secondary, #888D96)',
              lineHeight: '28px',
            }}
          >
            {t('MYPAGE.* 1일 최대 1회, 1회 최대 2시간, 1주 최대 4회 신청 가능')}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions sx={approvalDialogActionsSx}>
        <Button
          variant="outlined"
          size="medium"
          onClick={onClose}
          sx={actionButtonSx}
        >
          {t('MYPAGE.닫기')}
        </Button>
        <Button
          variant="contained"
          size="medium"
          sx={actionButtonSx}
          disabled={isSubmitDisabled}
          onClick={handleSubmit}
        >
          {t('MYPAGE.신청 완료')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OvertimeAdjustModal;
